import { useState } from 'react'
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react'
import { ScoopText, ScoopColumn, ScoopRow, ScoopButton } from '../../../../../../ui'
import styles from './QueryDetails.module.scss'

export const QueryDetails = ({ message, contentText }) => {
    const [queryDetailsExpanded, setQueryDetailsExpanded] = useState(false)

    // Check if this is a table visualization with vizTable data
    const vizTable = contentText?.vizTable
    const isTableViz = vizTable && (vizTable.view === 'table' || vizTable.selectedItems)

    const parseFilters = (filter, level = 0) => {
        if (!filter) return null

        // Single filter
        if (filter.attributeName) {
            const cssVars = level > 0 ? { '--filter-indent': `${level * 12}px` } : {}
            return (
                <ScoopText
                    key={`filter-${level}`}
                    className={`${styles.itemText} ${level > 0 ? styles.nestedFilter : ''}`}
                    style={Object.keys(cssVars).length > 0 ? cssVars : undefined}
                >
                    • {filter.attributeName} {filter.operator}{' '}
                    {filter.filterValue?.values?.join(', ')}
                </ScoopText>
            )
        }

        // Composite filter
        if (filter.filters && filter.filters.length > 0) {
            return filter.filters.map((f, idx) => parseFilters(f, level))
        }

        return null
    }

    return (
        <ScoopColumn className={styles.container}>
            <ScoopColumn className={styles.detailsBox}>
                <ScoopRow
                    className={`${styles.header} ${queryDetailsExpanded ? styles.expanded : styles.collapsed}`}
                    onClick={() => setQueryDetailsExpanded(!queryDetailsExpanded)}
                >
                    <ScoopText className={styles.title}>Query Details</ScoopText>
                    <ScoopButton className={styles.expandButton} variant="text" size="xxs">
                        {queryDetailsExpanded ? (
                            <CaretUpIcon size={16} />
                        ) : (
                            <CaretDownIcon size={16} />
                        )}
                    </ScoopButton>
                </ScoopRow>

                <ScoopColumn
                    className={`${styles.content} ${queryDetailsExpanded ? styles.contentExpanded : styles.contentCollapsed}`}
                >
                    {/* AI Description - Check vizTable first for tables */}
                    {(vizTable?.aiDescription ||
                        contentText?.aiDescription ||
                        contentText?.visualization?.aiDescription) && (
                        <ScoopColumn>
                            <ScoopText className={styles.aiDescription}>
                                {vizTable?.aiDescription ||
                                    contentText?.aiDescription ||
                                    contentText?.visualization?.aiDescription}
                            </ScoopText>
                        </ScoopColumn>
                    )}

                    {/* Dataset */}
                    {(message.content?.visualization?.datasetName ||
                        message.content?.visualization?.query?.datasetName ||
                        message.footer?.metadata?.dataset) && (
                        <ScoopRow className={styles.row}>
                            <ScoopText className={styles.label}>Dataset:</ScoopText>
                            <ScoopText className={styles.value}>
                                {message.content?.visualization?.datasetName ||
                                    message.content?.visualization?.query?.datasetName ||
                                    message.footer?.metadata?.dataset}
                            </ScoopText>
                        </ScoopRow>
                    )}

                    {/* Query Type */}
                    {(message.content?.visualization?.queryType ||
                        message.content?.visualization?.query?.queryType ||
                        vizTable?.categoryAxis ||
                        contentText?.visualization?.categoryAxis) && (
                        <ScoopRow className={styles.row}>
                            <ScoopText className={styles.label}>Type:</ScoopText>
                            <ScoopText className={styles.value}>
                                {message.content?.visualization?.queryType ||
                                    message.content?.visualization?.query?.queryType ||
                                    (vizTable?.view === 'table' ? 'table' : null) ||
                                    (vizTable?.categoryAxis !== 'Time'
                                        ? 'dataset'
                                        : 'timeseries') ||
                                    (contentText?.visualization?.categoryAxis !== 'Time'
                                        ? 'dataset'
                                        : 'timeseries')}
                            </ScoopText>
                        </ScoopRow>
                    )}

                    {/* Period */}
                    {(message.content?.visualization?.query?.period ||
                        vizTable?.period ||
                        contentText?.period ||
                        contentText?.visualization?.period) && (
                        <ScoopRow className={styles.row}>
                            <ScoopText className={styles.label}>Period:</ScoopText>
                            <ScoopText className={styles.value}>
                                {message.content?.visualization?.query?.period ||
                                    vizTable?.period ||
                                    contentText?.period ||
                                    contentText?.visualization?.period}
                            </ScoopText>
                        </ScoopRow>
                    )}

                    {/* Metrics */}
                    {(message.content?.visualization?.metrics?.length > 0 ||
                        message.content?.visualization?.query?.metrics?.length > 0 ||
                        vizTable?.selectedItems?.length > 0 ||
                        contentText?.visualization?.selectedItems?.length > 0) && (
                        <ScoopColumn className={styles.section}>
                            <ScoopText className={styles.sectionTitle}>Metrics:</ScoopText>
                            {/* Table format metrics from content.visualization */}
                            {message.content?.visualization?.metrics?.map((metric, idx) => (
                                <ScoopColumn key={idx} className={styles.itemContainer}>
                                    <ScoopText className={styles.itemText}>
                                        • {metric.label || metric.columnName}
                                        {metric.aggRule && ` (${metric.aggRule})`}
                                    </ScoopText>
                                </ScoopColumn>
                            ))}
                            {/* Chart format metrics */}
                            {message.content?.visualization?.query?.metrics?.map((metric, idx) => (
                                <ScoopColumn key={idx} className={styles.itemContainer}>
                                    <ScoopText className={styles.itemText}>
                                        • {metric.label || metric.columnName}
                                        {(metric.aggRule || metric.byDate) &&
                                            ` (${metric.aggRule || ''}${metric.byDate ? `${metric.aggRule ? ' ' : ''}by ${metric.byDate}` : ''})`}
                                    </ScoopText>
                                </ScoopColumn>
                            ))}
                            {/* vizTable selectedItems */}
                            {vizTable?.selectedItems?.map((metric, idx) => (
                                <ScoopColumn key={idx} className={styles.itemContainer}>
                                    <ScoopText className={styles.itemText}>
                                        • {metric.kpi || metric.label || metric.columnName}
                                    </ScoopText>
                                </ScoopColumn>
                            ))}
                            {/* Regular visualization selectedItems */}
                            {!vizTable?.selectedItems &&
                                contentText?.visualization?.selectedItems?.map((metric, idx) => (
                                    <ScoopColumn key={idx} className={styles.itemContainer}>
                                        <ScoopText className={styles.itemText}>
                                            • {metric.label || metric.columnName || metric.kpi}
                                            {(metric.aggRule || metric.byDate) &&
                                                ` (${metric.aggRule || ''}${metric.byDate ? `${metric.aggRule ? ' ' : ''}by ${metric.byDate}` : ''})`}
                                        </ScoopText>
                                    </ScoopColumn>
                                ))}
                        </ScoopColumn>
                    )}

                    {/* Attributes (for tables) */}
                    {(message.content?.visualization?.attributes?.length > 0 ||
                        vizTable?.selectedTableColumns?.length > 0) && (
                        <ScoopColumn className={styles.section}>
                            <ScoopText className={styles.sectionTitle}>Attributes:</ScoopText>
                            {/* Table format attributes */}
                            {message.content?.visualization?.attributes?.map((attr, idx) => (
                                <ScoopColumn key={idx} className={styles.itemContainer}>
                                    <ScoopText className={styles.itemText}>
                                        • {attr.label || attr.columnName}
                                    </ScoopText>
                                </ScoopColumn>
                            ))}
                            {/* vizTable selectedTableColumns */}
                            {vizTable?.selectedTableColumns?.map((col, idx) => (
                                <ScoopColumn key={idx} className={styles.itemContainer}>
                                    <ScoopText className={styles.itemText}>
                                        • {col.columnName}
                                        {col.tableName && ` (${col.tableName})`}
                                    </ScoopText>
                                </ScoopColumn>
                            ))}
                        </ScoopColumn>
                    )}

                    {/* Filters */}
                    {(message.content?.visualization?.query?.queryFilter ||
                        contentText?.visualization?.filter ||
                        contentText?.visualization?.description ||
                        vizTable?.description) && (
                        <ScoopColumn>
                            <ScoopText className={styles.sectionTitle}>Filters:</ScoopText>
                            {/* Use description if available (most readable) */}
                            {(vizTable?.description || contentText?.visualization?.description) && (
                                <ScoopText className={styles.filterDescription}>
                                    {(() => {
                                        const desc =
                                            vizTable?.description ||
                                            contentText?.visualization?.description
                                        // Parse different description formats
                                        if (desc.includes('; Metrics:')) {
                                            // Format: "Attributes: Stage; Metrics: Revenue"
                                            return desc
                                        } else if (desc.includes('; Filter:')) {
                                            // Format: "Metrics: ...; Filter: ..."
                                            return desc.replace(/^Metrics: [^;]+; Filter: /, '')
                                        }
                                        return desc
                                    })()}
                                </ScoopText>
                            )}
                            {/* Quick analysis format */}
                            {!contentText?.visualization?.description &&
                                message.content?.visualization?.query?.queryFilter && (
                                    <ScoopText className={styles.quickAnalysisFilter}>
                                        •{' '}
                                        {
                                            message.content.visualization.query.queryFilter
                                                .attributeName
                                        }{' '}
                                        {message.content.visualization.query.queryFilter.operator}{' '}
                                        {message.content.visualization.query.queryFilter.values?.join(
                                            ', '
                                        )}
                                    </ScoopText>
                                )}
                            {/* Parse complex filter structure if no description */}
                            {!contentText?.visualization?.description &&
                                !message.content?.visualization?.query?.queryFilter &&
                                contentText?.visualization?.filter && (
                                    <ScoopColumn className={styles.filterContainer}>
                                        {parseFilters(contentText.visualization.filter)}
                                    </ScoopColumn>
                                )}
                        </ScoopColumn>
                    )}
                </ScoopColumn>
            </ScoopColumn>
        </ScoopColumn>
    )
}
